import {
  HttpErrorResponse,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent,
} from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { BvAlertService } from '@ziptility/bv-alert'
import { JWT, AuthCookieService } from '@ziptility/modules'
import { isEqual, get, includes, any, isString, isArray } from 'lodash/fp'
import { Observable, throwError, BehaviorSubject, EMPTY } from 'rxjs'
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators'
import { AuthService } from '@ziptility/services'
import { StandardizedError } from './models/error.model'

@Injectable()
export class RequestInterceptorService implements HttpInterceptor {
  private alertService = inject(BvAlertService)
  private cookieService = inject(AuthCookieService)
  private authService = inject(AuthService)

  isRefreshingToken = false
  readonly tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null)

  private hasInvalidTokenGraphQLError(response: HttpErrorResponse) {
    const errors = get('error.errors', response)
    if (errors && errors.length) {
      return any((e: any) => e.extensions && e.extensions.code === 'AUTH_NOT_AUTHENTICATED', errors)
    }
    return false
  }

  setHeaders(req: HttpRequest<any>, access_token: string): HttpRequest<any> {
    if (includes('https://s3.amazonaws.com', req.url)) {
      return req
    } else {
      return req.clone({
        setHeaders: {
          Authorization: `Bearer ${access_token}`,
        },
      })
    }
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
    return next.handle(this.setHeaders(req, this.cookieService.accessToken())).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          const status = (<HttpErrorResponse>error).status
          const isReadRequest = ['GET', 'HEAD', 'OPTIONS'].includes(req.method)

          switch (status) {
            case 0:
              this.alertService.error('Network Error', true)
              break
            case 401:
              return this.handle401Error((newToken: string) => next.handle(this.setHeaders(req, newToken)))
            case 400:
              if (
                isEqual('invalid_grant', get('error.error', error)) ||
                isEqual('Your token is invalid. Please login', get('error.message', error))
              ) {
                this.authService.logout()
                return EMPTY
              }

              this.alertService.error(this.createAlertMessage(error), isReadRequest)
              break
            case 403:
              // this.showError("You don't have permission to access")
              break
            default:
              if (this.hasInvalidTokenGraphQLError(error)) {
                break
              }
              this.alertService.error(this.createAlertMessage(error), isReadRequest)
              break
          }
        }

        return throwError(error)
      }),
    )
  }

  handle401Error(onRefreshTokenSuccess: (accessToken: string) => Observable<any>) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null)

      const refreshToken = this.cookieService.refreshToken()
      if (!refreshToken) {
        this.authService.logout()
        return EMPTY
      }

      return this.authService.refresh(refreshToken).pipe(
        switchMap((authData: JWT) => {
          if (authData) {
            this.tokenSubject.next(authData.access_token)
            return onRefreshTokenSuccess(authData.access_token)
          }

          this.authService.logout()
          return EMPTY
        }),
        catchError((error) => {
          console.error('Error refreshing token', error)
          this.authService.logout()
          return EMPTY
        }),
        finalize(() => {
          this.isRefreshingToken = false
        }),
      )
    } else {
      return this.tokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          return onRefreshTokenSuccess(token)
        }),
      )
    }
  }

  private createAlertMessage(response: HttpErrorResponse): string | StandardizedError {
    const error = response.error

    // Check if backend already returned standardized error format
    if (error && typeof error === 'object') {
      // If it has message and details array, treat as standardized
      if (
        isString(error.message) &&
        error.details !== undefined &&
        isArray(error.details)
      ) {
        return {
          message: error.message,
          details: error.details,
        }
      }

      // If it has message and errors array (common GraphQL/validation pattern)
      if (isString(error.message) && isArray(error.errors) && error.errors.length > 0) {
        return {
          message: error.message,
          details: error.errors,
        }
      }

      // Legacy format: message as string
      if (isString(error.message)) {
        return error.message
      }

      // Legacy format: message as array of strings
      if (isArray(error.message)) {
        return error.message.join(', ')
      }
    }

    return 'Something went wrong. Please try again.'
  }
}
